import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { env } from '@/env';
import { getAccessToken } from '@/stores/auth-store';

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface UseNotificationStreamOptions {
  enabled: boolean;
  /**
   * Called on every `notification` event received from the stream.
   * Usually invalidates the inbox query + (optionally) fires a
   * desktop notification.
   */
  onNotification: (payload: { id: string; ts: string }) => void;
}

/**
 * Subscribe to `/notifications/me/stream` via Server-Sent Events
 * (ADR-0050). Lifecycle:
 *
 *   1. Opens an EventSource as soon as `enabled` flips true and the
 *      access token is available. Token is passed via `?token=` since
 *      browser EventSource can't send custom headers.
 *   2. On 'notification' event: fires `onNotification` with the
 *      payload + invalidates the inbox React Query so the bell
 *      refetches.
 *   3. EventSource auto-reconnects when the stream drops. Each
 *      reconnect lands us back at step 2 - we also do a one-off
 *      inbox refetch on every reconnect so any rows the server
 *      published mid-drop are picked up.
 *   4. When `enabled` flips false (logout / unmount), the stream is
 *      closed cleanly.
 *
 * The hook gracefully no-ops when the server responds 503
 * (Redis offline) - the polling fallback in `useMyNotificationsInbox`
 * keeps the inbox fresh, just less promptly.
 */
export const useNotificationStream = ({
  enabled,
  onNotification,
}: UseNotificationStreamOptions): { status: StreamStatus } => {
  const qc = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>('idle');
  // Keep refs to the latest callbacks/values so the effect can stay
  // dependency-light (we don't want a reconnect every time onNotification
  // identity flips on a parent re-render).
  const onNotificationRef = useRef(onNotification);
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    if (!enabled) return;
    const token = getAccessToken();
    if (!token) return;

    setStatus('connecting');
    const url = `${env.VITE_API_BASE_URL}/notifications/me/stream?token=${encodeURIComponent(token)}`;
    const source = new EventSource(url);

    source.addEventListener('open', () => {
      setStatus('open');
      // Pick up anything we missed during connection setup.
      qc.invalidateQueries({ queryKey: ['notifications', 'me', 'inbox'] });
    });

    source.addEventListener('notification', (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as { id: string; ts: string };
        onNotificationRef.current(data);
      } catch {
        // Bad payload - still kick a refetch so the bell catches up.
      }
      qc.invalidateQueries({ queryKey: ['notifications', 'me', 'inbox'] });
    });

    source.addEventListener('error', () => {
      // EventSource auto-reconnects unless the server returned a
      // permanent error (4xx). Browser only tells us via `readyState`.
      if (source.readyState === EventSource.CLOSED) {
        setStatus('error');
      } else {
        setStatus('connecting');
      }
    });

    return () => {
      source.close();
      setStatus('closed');
    };
  }, [enabled, qc]);

  return { status };
};
